import {
  FRAMEWORK_PACK_CATALOG,
  findFrameworkPackByText,
  type FrameworkPackDefinition,
  type FrameworkPackId,
} from "@/lib/behavior/frameworkPacks";
import type { CoachVoiceDraft } from "@/lib/behavior/coachVoiceDraft";

export const COACH_IDENTITY_DRAFT_STORAGE_PREFIX = "coach-identity-guided-form-draft";

export type CoachIdentityGuidedDraft = {
  version: 1;
  savedAt: string;
  data: {
    lensName: string;
    lensPhilosophy: string;
    lensFrameworksRaw: string;
    selectedFrameworkPackId?: FrameworkPackId | null;
    preferredLanguageText: string;
    avoidLanguageText: string;
    linksText: string;
    draftTwinProfile: CoachVoiceDraft;
    questionStemsText: string;
    phraseBankText: string;
  };
};

export type ActivationConfirmation = {
  activatedAt: string;
  lensLabel: string;
  voiceSummary: string | null;
  parsedReferenceCount: number;
  referenceCount: number;
};

export type CoachArtifactExtractionRow = {
  artifact_id: string;
  extraction_json: {
    voice_summary?: string | null;
    question_stems?: string[] | null;
    phrase_bank?: string[] | null;
    avoid_list?: string[] | null;
    coaching_moves?: string[] | null;
    guardrails?: string[] | null;
  } | null;
  created_at: string;
};

export function buildNewestCoachArtifactExtractionMap(
  rows: CoachArtifactExtractionRow[] | undefined,
): Map<string, CoachArtifactExtractionRow> {
  const extractionByArtifactId = new Map<string, CoachArtifactExtractionRow>();

  for (const row of rows || []) {
    const existing = extractionByArtifactId.get(row.artifact_id);
    if (!existing || row.created_at > existing.created_at) {
      extractionByArtifactId.set(row.artifact_id, row);
    }
  }

  return extractionByArtifactId;
}

export function toDisplayError(error: unknown) {
  if (!error) return "Unknown error";
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  const err = error as { message?: string; details?: string; hint?: string; code?: string };
  if (err.message || err.details || err.hint) {
    return [err.message, err.details, err.hint, err.code].filter(Boolean).join(" • ");
  }
  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown error";
  }
}

export function isUnauthorizedFunctionError(error: unknown) {
  if (!error) return false;
  const err = error as {
    message?: string;
    details?: string;
    hint?: string;
    code?: string | number;
    status?: number;
    context?: { status?: number };
  };
  const status = err.status ?? err.context?.status;
  if (status === 401 || err.code === 401 || err.code === "401") return true;
  const message = [err.message, err.details, err.hint, err.code].filter(Boolean).join(" ").toLowerCase();
  return (
    message.includes("401")
    || message.includes("unauthorized")
    || message.includes("not authenticated")
    || message.includes("jwt")
  );
}

export function buildArtifactPrefill(extractions: CoachArtifactExtractionRow[]) {
  const seen = {
    questionStems: new Set<string>(),
    phraseBank: new Set<string>(),
    avoidList: new Set<string>(),
    coachingMoves: new Set<string>(),
    guardrails: new Set<string>(),
  };

  const merged = {
    voiceSummary: "",
    questionStems: [] as string[],
    phraseBank: [] as string[],
    avoidList: [] as string[],
    coachingMoves: [] as string[],
    guardrails: [] as string[],
  };

  const mergeList = (
    values: string[] | null | undefined,
    bucket: string[],
    seenValues: Set<string>,
  ) => {
    for (const value of values || []) {
      const normalized = value.trim();
      if (!normalized || seenValues.has(normalized)) continue;
      seenValues.add(normalized);
      bucket.push(normalized);
    }
  };

  for (const extraction of buildNewestCoachArtifactExtractionMap(extractions).values()) {
    const payload = extraction.extraction_json || {};
    if (!merged.voiceSummary && typeof payload.voice_summary === "string" && payload.voice_summary.trim()) {
      merged.voiceSummary = payload.voice_summary.trim();
    }
    mergeList(payload.question_stems, merged.questionStems, seen.questionStems);
    mergeList(payload.phrase_bank, merged.phraseBank, seen.phraseBank);
    mergeList(payload.avoid_list, merged.avoidList, seen.avoidList);
    mergeList(payload.coaching_moves, merged.coachingMoves, seen.coachingMoves);
    mergeList(payload.guardrails, merged.guardrails, seen.guardrails);
  }

  return merged;
}

export function parseList(value: string): string[] {
  return value.split("\n").map((item) => item.replace(/^[-*]\s*/, "").trim()).filter(Boolean);
}

export function parseLinkLines(value: string) {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [title, sourceUrl, notes] = line.split("|").map((item) => item.trim());
      return {
        title: title || sourceUrl || "",
        sourceUrl: sourceUrl || title || "",
        notes: notes ? notes.split(";").map((item) => item.trim()).filter(Boolean) : [],
      };
    });
}

export function resolveFrameworkPackSelection(
  selectedFrameworkPackId: FrameworkPackId | null,
  lensFrameworksRaw: string,
): FrameworkPackDefinition | null {
  if (selectedFrameworkPackId) {
    return FRAMEWORK_PACK_CATALOG.find((pack) => pack.id === selectedFrameworkPackId) || null;
  }

  const frameworkLines = parseList(lensFrameworksRaw);
  if (frameworkLines.length !== 1) {
    return null;
  }

  return findFrameworkPackByText(frameworkLines[0]);
}

export function resolveVoiceCustomizationStatus(params: {
  draftTwinProfile: CoachVoiceDraft;
  questionStemsText: string;
  phraseBankText: string;
  resolvedFrameworkPack: FrameworkPackDefinition | null;
}): "customized" | "framework_default" | "default" {
  const hasCustomVoiceContent = Boolean(
    params.draftTwinProfile.voiceSummary.trim()
      || parseList(params.questionStemsText).length
      || parseList(params.phraseBankText).length
      || params.draftTwinProfile.avoidList.length
      || params.draftTwinProfile.coachingMoves.length
      || params.draftTwinProfile.guardrails.length,
  );

  if (hasCustomVoiceContent) {
    return "customized";
  }

  return params.resolvedFrameworkPack ? "framework_default" : "default";
}
